import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import type { CategoriaVista } from '@shared/types';
import { FormCategoria } from '@renderer/components/forms/FormCategoria';

export function CategoriasPage() {
  const [items, setItems] = useState<CategoriaVista[]>([]);
  const [editando, setEditando] = useState<CategoriaVista | null>(null);
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    const res = await window.api.categorias.listar();
    if (res.ok) setItems(res.data);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  const onNuevo = () => {
    setEditando(null);
    setDrawerAbierto(true);
  };

  const onEditar = (c: CategoriaVista) => {
    setEditando(c);
    setDrawerAbierto(true);
  };

  const onEliminar = async (c: CategoriaVista) => {
    const ok = await window.confirmar({ titulo: 'Eliminar categoría', mensaje: `¿Eliminar la categoría "${c.nombre}"?`, textoConfirmar: 'Eliminar', variant: 'destructive' });
    if (!ok) return;
    const res = await window.api.categorias.eliminar(c.id);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'No se pudo eliminar', mensaje: res.error, variant: 'warn' });
  };

  const onSaved = () => {
    setDrawerAbierto(false);
    cargar();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Categorías</h1>
          <p className="text-sm text-slate-500">Agrupación visual del menú.</p>
        </div>
        <button onClick={onNuevo} className="btn-primary">
          <Plus className="h-4 w-4" />
          Nueva categoría
        </button>
      </div>

      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Color</th>
              <th className="px-4 py-3">Icono</th>
              <th className="px-4 py-3">Posición</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">Cargando...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center">
                  {/* Sprint 62 — Un texto gris no dice para qué sirve esto ni qué hacer. */}
                  <p className="font-medium text-slate-600">Todavía no hay categorías</p>
                  <p className="mt-1 text-sm text-slate-500">
                    Agrupan los botones del punto de venta: Carnes, Bebidas, Postres. Son
                    opcionales — sin ellas, todo aparece en una sola lista.
                  </p>
                </td>
              </tr>
            ) : (
              items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{c.nombre}</td>
                  <td className="px-4 py-3">
                    {c.color && (
                      <span
                        className="inline-block h-5 w-5 rounded-full border border-slate-200"
                        style={{ backgroundColor: c.color }}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{c.icono ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{c.posicion ?? '—'}</td>
                  <td className="px-4 py-3">
                    {c.habilitado ? (
                      <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">Habilitada</span>
                    ) : (
                      <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-700">Deshabilitada</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => onEditar(c)} className="btn-ghost p-2" aria-label="Editar">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => onEliminar(c)} className="btn-ghost p-2 text-red-600" aria-label="Eliminar">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <FormCategoria
        abierto={drawerAbierto}
        categoria={editando}
        onClose={() => setDrawerAbierto(false)}
        onSaved={onSaved}
      />
    </div>
  );
}
